import React, { useState, useEffect } from 'react';
import { MenuItem } from '@blueprintjs/core';
import { HyperlinkButton } from 'common-components/buttons';
import apiClient from 'utilities/api-client';

interface CreditNotesMenuItemProps {
  invoiceNumber: string;
  serviceProviderId: string;
  onOpenModal: () => void;
}

export const CreditNotesMenuItem: React.FC<CreditNotesMenuItemProps> = ({
  invoiceNumber,
  serviceProviderId,
  onOpenModal,
}) => {
  const [hasCreditNotes, setHasCreditNotes] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasChecked, setHasChecked] = useState<boolean>(false);

  const checkForCreditNotes = async () => {
    if (hasChecked || isLoading) return;

    setIsLoading(true);
    try {
      const response = await apiClient.post('/api/portal/billing/credit-notes/list', {
        serviceProviderId,
        invoiceNumber,
        page: 1,
        pageSize: 1, // We only need to know if any exist
      });

      if (response.status === 200) {
        const responseData = response.data as { creditNotes?: unknown[]; total?: number };
        const creditNotesExist = responseData?.creditNotes && responseData.creditNotes.length > 0;
        setHasCreditNotes(creditNotesExist);
      }
    } catch (error) {
      // If there's an error checking for credit notes, assume none exist
      setHasCreditNotes(false);
    } finally {
      setIsLoading(false);
      setHasChecked(true);
    }
  };

  // Only check for credit notes when this component is rendered (i.e., when menu is opened)
  useEffect(() => {
    checkForCreditNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasCreditNotes && hasChecked) {
    return null;
  }

  return (
    <MenuItem
      text={
        <div
          onClick={() => {
            onOpenModal();
          }}
          onKeyPress={() => {
            onOpenModal();
          }}
          // biome-ignore lint/a11y/useSemanticElements: used for keyboard accessibility
          role='button'
          tabIndex={0}
        >
          <HyperlinkButton>{isLoading ? 'Loading...' : 'View Credit Notes'}</HyperlinkButton>
        </div>
      }
      className='hover-bg-gray-lightest'
    />
  );
};
